import { ILessonPlanNode } from "./useLessonPlanEditor/useLessonPlanEditor";

const mockLessonPlan: ILessonPlanNode = {
  type: "doc",
  content: [
    {
      type: "headings_block",
      content: [
        {
          type: "text",
          text: "Objective:",
        },
      ],
      attrs: {
        level: 4,
      },
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Students will determine the central idea of the article about how scientists use AI to communicate with animals, analyze how this idea develops throughout the text, and cite textual evidence to support their analysis and make logical inferences.",
        },
      ],
    },
    {
      type: "headings_block",
      content: [
        {
          type: "text",
          text: "Reading instructions:",
        },
      ],
      attrs: {
        level: 4,
      },
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "As you read the article, choose one color to highlight examples of how technology is used to understand animal communication and another color to highlight the different ways animals communicate that are described. Annotate to explain how the technology helps reveal these communication methods and why understanding animal communication matters. While reading, consider: What is the main idea the author wants to convey about AI and animal communication? How does the article develop this idea through examples and explanations?",
        },
      ],
    },
    {
      type: "headings_block",
      content: [
        {
          type: "text",
          text: "Background knowledge:",
        },
      ],
      attrs: {
        level: 4,
      },
    },
    {
      type: "list_block_0",
      content: [
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Basic understanding of animal communication methods (e.g., sounds, gestures).",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Familiarity with artificial intelligence and its general applications.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Concept of anthropocentrism versus biocentrism in studying animals.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Awareness of scientific research methods and technology used in biology.",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      type: "headings_block",
      content: [
        {
          type: "text",
          text: "Project idea",
        },
      ],
      attrs: {
        level: 4,
      },
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Research Project: Investigate another example of technology used in animal communication studies and present findings in a multimedia presentation.",
        },
      ],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Creative Writing: Write a fictional dialogue between a scientist and an animal using AI communication technology, incorporating facts from the article.",
        },
      ],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Multimedia Project: Create an infographic that explains how AI helps decode animal communication and why it matters for conservation.",
        },
      ],
    },
    {
      type: "headings_block",
      content: [
        {
          type: "text",
          text: "Supports for Multilingual Learners",
        },
      ],
      attrs: {
        level: 4,
      },
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Provide sentence frames for writing annotations and summaries, such as: 'The technology helps scientists by ___. This is important because ___.', 'Animals communicate by ___. AI helps us understand this by ___.',",
        },
      ],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Use visuals such as images of animals mentioned (bats, bees, gorillas) and diagrams of AI technology to support comprehension.",
        },
      ],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Encourage structured partner work where students discuss their annotations and main ideas before writing, to build oral language and confidence.",
        },
      ],
    },
    {
      type: "headings_block",
      content: [
        {
          type: "text",
          text: "Activity Ideas:",
        },
      ],
      attrs: {
        level: 4,
      },
    },
    {
      type: "list_block_0",
      content: [
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Main Idea and Key Details: Identify the central idea of the article and find three key details that support this idea. Use the ",
                },
                {
                  type: "text",
                  text: "Main Idea and Key Details graphic organizer ",
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: "graphicOrganizer=main_idea_key_details",
                      },
                    },
                  ],
                },
                {
                  type: "text",
                  text: "to organize your findings.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Textual Evidence Analysis: Select two examples from the article where technology reveals new insights about animal communication. Write a short paragraph explaining how the evidence supports the article’s main idea.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Summary Writing: Write a concise summary of the article in your own words, focusing on the development of the central idea and key supporting details.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Discussion Questions: Answer these questions in writing: Why do some researchers criticize attempts to teach animals human language? How does the use of AI change our understanding of animal communication?",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Compare and Contrast: Compare traditional methods of studying animal communication with the new AI-based methods described in the article. Use the ",
                },
                {
                  type: "text",
                  text: "Compare and Contrast graphic organizer ",
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: "graphicOrganizer=compare_contrast",
                      },
                    },
                  ],
                },
                {
                  type: "text",
                  text: "to organize your thoughts.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Vocabulary Context Clues: Identify five challenging words from the article related to technology or animal communication. Use the ",
                },
                {
                  type: "text",
                  text: "Context Clues graphic organizer ",
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: "graphicOrganizer=context_clues",
                      },
                    },
                  ],
                },
                {
                  type: "text",
                  text: "to define them based on how they are used in the text.",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      type: "headings_block",
      content: [
        {
          type: "text",
          text: "Lesson breakdown:",
        },
      ],
      attrs: {
        level: 4,
      },
    },
    {
      type: "list_block_0",
      content: [
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Introduction and Hook (10 min): Begin by asking students if they think animals can communicate like humans and what tools might help us understand them better. Show a short video clip or image of Koko the gorilla using sign language to spark interest. Present the learning objective and explain that they will explore how AI is helping scientists decode animal communication.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Pre-Reading Activity (5 min): Have students complete the ",
                },
                {
                  type: "text",
                  text: "Know Wonder Learn graphic organizer ",
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: "graphicOrganizer=know_wonder_learn",
                      },
                    },
                  ],
                },
                {
                  type: "text",
                  text: "by writing what they KNOW about animal communication and AI, and what they WONDER about how technology might help us understand animals.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Guided Reading (20 min): Provide the reading instructions and have students read the article independently or in pairs. Remind them to highlight examples of technology use and animal communication methods, and to annotate their thinking about how these connect to the main idea. Circulate to support comprehension and clarify vocabulary.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Main Idea and Evidence Activity (15 min): Students complete the ",
                },
                {
                  type: "text",
                  text: "Main Idea and Key Details graphic organizer ",
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: "graphicOrganizer=main_idea_key_details",
                      },
                    },
                  ],
                },
                {
                  type: "text",
                  text: "to identify the central idea and supporting details. Then, have them write a short paragraph citing textual evidence that supports their analysis of the article’s message.",
                },
              ],
            },
          ],
        },
        {
          type: "list_block_1",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Closure and Reflection (10 min): Lead a class discussion or think-pair-share where students share their main ideas and evidence. Ask guiding questions such as: How has AI changed the way we think about animal communication? What implications does this have for humans’ relationship with other species? Finally, have students complete the 'Learned' section of the Know Wonder Learn graphic organizer.",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export default mockLessonPlan;
